import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading } from "../../components/VB";
import MutasiTable from "../../components/MutasiTable";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet, ArrowRightLeft, RefreshCw, Pencil, ListTree, DollarSign, Coins, PiggyBank } from "lucide-react";

const fmtIDR = (n) => "Rp " + (parseFloat(n || 0)).toLocaleString("id-ID", { maximumFractionDigits: 0 });
const fmtUSD = (n) => "$ " + (parseFloat(n || 0)).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtCur = (n, cur) => (cur === "USD" ? fmtUSD(n) : fmtIDR(n));

const KATEGORI_MASUK = ["Penjualan", "Investasi", "Penerimaan Piutang", "Pendapatan Lain", "Lainnya"];
const KATEGORI_KELUAR = ["Penggajian", "Operasional", "Sewa", "Listrik & Air", "Internet", "Marketing", "Bonus", "Pembelian", "Lainnya"];

export default function ArusKas() {
  const [tab, setTab] = useState("rekening");
  const [rates, setRates] = useState({ IDR: 0, USD: 1 });
  const [display, setDisplay] = useState("IDR");
  const [reks, setReks] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRates = async () => {
    try {
      const { data } = await api.get("/exchange-rate");
      setRates(data.rates || { IDR: 0, USD: 1 });
    } catch (e) { /* keep silent */ }
  };
  const loadReks = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rekening");
      setReks(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { loadRates(); loadReks(); }, []);

  const convert = (n, from, to = display) => {
    const v = parseFloat(n || 0);
    if (from === to) return v;
    const rateIDR = parseFloat(rates.IDR || 16000);
    if (from === "USD" && to === "IDR") return v * rateIDR;
    if (from === "IDR" && to === "USD") return v / rateIDR;
    return v;
  };

  // Tabungan Kepala Group (tabungan_user_id) = uang titipan client, TIDAK ikut total saldo perusahaan.
  const totalSaldoDisplay = useMemo(() => reks.filter((r) => r.status_aktif && !r.tabungan_user_id).reduce((a, r) => a + convert(r.saldo, r.currency, display), 0), [reks, rates, display]);

  return (
    <Window
      title="Sistem Keuangan & Multi-Rekening"
      actions={
        <>
          <button className={`vb-btn ${tab === "rekening" ? "vb-btn-primary" : ""}`} onClick={() => setTab("rekening")} data-testid="tab-rekening">
            <Wallet className="w-3 h-3 inline mr-1" />Rekening
          </button>
          <button className={`vb-btn ${tab === "mutasi" ? "vb-btn-primary" : ""}`} onClick={() => setTab("mutasi")} data-testid="tab-mutasi">
            <ListTree className="w-3 h-3 inline mr-1" />Mutasi
          </button>
          <button className={`vb-btn ${tab === "cashflow" ? "vb-btn-primary" : ""}`} onClick={() => setTab("cashflow")} data-testid="tab-cashflow">
            <ArrowRightLeft className="w-3 h-3 inline mr-1" />Arus Kas Manual
          </button>
          <button className="vb-btn" onClick={loadRates} title="Refresh kurs" data-testid="refresh-rates"><RefreshCw className="w-3 h-3" /></button>
        </>
      }
    >
      {/* Rate banner */}
      <div style={{ background: "#fffbe6", border: "1px solid #d4a017", padding: "6px 10px", marginBottom: 10, fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
        <span>
          <strong>Kurs USD → IDR:</strong> {fmtIDR(rates.IDR)} <span style={{ color: "#666", fontSize: 11 }}>(api.exchangerate.fun)</span>
        </span>
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          Tampilkan total saldo dalam:
          <select className="vb-select" style={{ width: 90 }} value={display} onChange={(e) => setDisplay(e.target.value)} data-testid="display-currency">
            <option>IDR</option>
            <option>USD</option>
          </select>
          <strong style={{ color: totalSaldoDisplay >= 0 ? "#003399" : "#d8000c", fontSize: 14 }} data-testid="total-saldo">
            {fmtCur(totalSaldoDisplay, display)}
          </strong>
        </span>
      </div>

      {tab === "rekening" && <RekeningTab reks={reks} loadReks={loadReks} loading={loading} convert={convert} display={display} />}
      {tab === "mutasi" && <MutasiTab reks={reks} />}
      {tab === "cashflow" && <CashflowTab reks={reks} convert={convert} display={display} />}
    </Window>
  );
}

// ============ REKENING TAB ============
function RekeningTab({ reks, loadReks, loading, convert, display }) {
  const today = new Date().toISOString().slice(0, 10);
  const empty = { nama: "", bank_nama: "", no_rekening: "", currency: "IDR", saldo_awal: 0, status_aktif: true, catatan: "" };
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);

  const [topupOpen, setTopupOpen] = useState(null); // rekening obj
  const [topupForm, setTopupForm] = useState({ tanggal: today, nominal: 0, deskripsi: "" });

  const [wdOpen, setWdOpen] = useState(null);
  const [wdForm, setWdForm] = useState({ tanggal: today, nominal: 0, deskripsi: "" });

  const [trfOpen, setTrfOpen] = useState(false);
  const [trfForm, setTrfForm] = useState({ from_rekening_id: "", to_rekening_id: "", tanggal: today, nominal: 0, deskripsi: "" });

  const save = async (e) => {
    e?.preventDefault?.();
    try {
      if (editId) await api.put(`/rekening/${editId}`, form);
      else await api.post("/rekening", { ...form, saldo_awal: parseFloat(form.saldo_awal || 0) });
      toast.success("Rekening tersimpan"); setOpen(false); loadReks();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/rekening/${del.id}`); toast.success("Dihapus"); setDel(null); loadReks(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doTopup = async () => {
    try {
      await api.post(`/rekening/${topupOpen.id}/topup`, { rekening_id: topupOpen.id, ...topupForm, nominal: parseFloat(topupForm.nominal) });
      toast.success("Topup berhasil"); setTopupOpen(null); setTopupForm({ tanggal: today, nominal: 0, deskripsi: "" }); loadReks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doWithdraw = async () => {
    try {
      await api.post(`/rekening/${wdOpen.id}/withdraw`, { rekening_id: wdOpen.id, ...wdForm, nominal: parseFloat(wdForm.nominal) });
      toast.success("Pengeluaran tercatat"); setWdOpen(null); setWdForm({ tanggal: today, nominal: 0, deskripsi: "" }); loadReks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doTransfer = async () => {
    try {
      const r = await api.post(`/rekening/transfer`, { ...trfForm, nominal: parseFloat(trfForm.nominal) });
      const src = reks.find((x) => x.id === trfForm.from_rekening_id);
      const dst = reks.find((x) => x.id === trfForm.to_rekening_id);
      toast.success(`Transfer berhasil. ${fmtCur(r.data.amount_src, src?.currency)} → ${fmtCur(r.data.amount_dst, dst?.currency)}`);
      setTrfOpen(false); setTrfForm({ from_rekening_id: "", to_rekening_id: "", tanggal: today, nominal: 0, deskripsi: "" }); loadReks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const companyReks = reks.filter((r) => !r.tabungan_user_id);
  const tabunganReks = reks.filter((r) => r.tabungan_user_id);

  if (loading) return <Loading />;
  return (
    <>
      <div style={{ marginBottom: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button className="vb-btn-primary" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-rekening">
          <Plus className="w-3 h-3 inline mr-1" />Buka Rekening Baru
        </button>
        <button className="vb-btn" onClick={() => { setTrfForm({ from_rekening_id: reks[0]?.id || "", to_rekening_id: reks[1]?.id || "", tanggal: today, nominal: 0, deskripsi: "" }); setTrfOpen(true); }} data-testid="transfer-btn" disabled={reks.length < 2}>
          <ArrowRightLeft className="w-3 h-3 inline mr-1" />Transfer Antar Rekening
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 10 }}>
        {companyReks.length === 0 && <EmptyState message="Belum ada rekening. Klik 'Buka Rekening Baru'." />}
        {companyReks.map((r) => (
          <div key={r.id} className="vb-window" style={{ margin: 0, opacity: r.status_aktif ? 1 : 0.55 }} data-testid={`rk-card-${r.id}`}>
            <div className="vb-titlebar">
              <span>
                {r.currency === "USD" ? <DollarSign className="w-3 h-3 inline mr-1" /> : <Coins className="w-3 h-3 inline mr-1" />}
                {r.nama}
              </span>
              <span className="vb-badge vb-badge-izin" style={{ background: r.currency === "USD" ? "#1a73e8" : "#4f8a10", color: "white", border: "none" }}>
                {r.currency}
              </span>
            </div>
            <div className="vb-content" style={{ padding: 10 }}>
              <div style={{ fontSize: 11, color: "#666" }}>{r.bank_nama} {r.no_rekening && `· ${r.no_rekening}`}</div>
              <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700, color: r.saldo >= 0 ? "#003399" : "#d8000c" }}>
                {fmtCur(r.saldo, r.currency)}
              </div>
              {display !== r.currency && (
                <div style={{ fontSize: 11, color: "#666", fontStyle: "italic" }}>
                  ≈ {fmtCur(convert(r.saldo, r.currency, display), display)}
                </div>
              )}
              <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
                <button className="vb-btn" style={{ padding: "3px 8px", fontSize: 11 }} onClick={() => { setTopupOpen(r); setTopupForm({ tanggal: today, nominal: 0, deskripsi: "" }); }} data-testid={`topup-${r.id}`}>
                  <TrendingUp className="w-3 h-3 inline mr-1 text-green-600" />Topup
                </button>
                <button className="vb-btn" style={{ padding: "3px 8px", fontSize: 11 }} onClick={() => { setWdOpen(r); setWdForm({ tanggal: today, nominal: 0, deskripsi: "" }); }} data-testid={`withdraw-${r.id}`}>
                  <TrendingDown className="w-3 h-3 inline mr-1 text-red-600" />Keluar
                </button>
                <button className="vb-btn" style={{ padding: "3px 8px", fontSize: 11 }} onClick={() => { setForm(r); setEditId(r.id); setOpen(true); }} data-testid={`edit-rk-${r.id}`}>
                  <Pencil className="w-3 h-3 inline mr-1" />Edit
                </button>
                <button className="vb-btn-danger" style={{ padding: "3px 8px", fontSize: 11 }} onClick={() => setDel(r)} data-testid={`del-rk-${r.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabungan Kepala Group — dibuat otomatis per akun Kepala Group, tidak ikut total saldo */}
      <div style={{ marginTop: 16, marginBottom: 6, fontWeight: 700, fontSize: 13, color: "#003399" }}>
        <PiggyBank className="w-4 h-4 inline mr-1" />Tabungan Kepala Group
        <span style={{ fontWeight: 400, fontSize: 11, color: "#666", marginLeft: 8 }}>
          Dibuat otomatis untuk setiap akun Kepala Group (USD). Tidak ikut Total Saldo perusahaan. Kepala Group bisa melihat saldo & riwayatnya di menu Tabungan, tapi hanya admin yang bisa Kredit/Debit.
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 10 }}>
        {tabunganReks.length === 0 && <EmptyState message="Belum ada akun Kepala Group. Tautkan akun Pengguna ke Group Client di Payment Website → Pengaturan." />}
        {tabunganReks.map((r) => (
          <div key={r.id} className="vb-window" style={{ margin: 0, opacity: r.status_aktif ? 1 : 0.55 }} data-testid={`tab-card-${r.id}`}>
            <div className="vb-titlebar">
              <span><PiggyBank className="w-3 h-3 inline mr-1" />{r.nama}</span>
              <span className="vb-badge vb-badge-izin" style={{ background: "#1a73e8", color: "white", border: "none" }}>{r.currency}</span>
            </div>
            <div className="vb-content" style={{ padding: 10 }}>
              <div style={{ fontSize: 11, color: "#666" }}>{r.no_rekening}{!r.status_aktif && " · bukan Kepala Group lagi"}</div>
              <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700, color: r.saldo >= 0 ? "#003399" : "#d8000c" }}>
                {fmtCur(r.saldo, r.currency)}
              </div>
              {display !== r.currency && (
                <div style={{ fontSize: 11, color: "#666", fontStyle: "italic" }}>≈ {fmtCur(convert(r.saldo, r.currency, display), display)}</div>
              )}
              <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
                <button className="vb-btn" style={{ padding: "3px 8px", fontSize: 11 }} onClick={() => { setTopupOpen(r); setTopupForm({ tanggal: today, nominal: 0, deskripsi: "" }); }} data-testid={`tab-kredit-${r.id}`}>
                  <TrendingUp className="w-3 h-3 inline mr-1 text-green-600" />Kredit
                </button>
                <button className="vb-btn" style={{ padding: "3px 8px", fontSize: 11 }} onClick={() => { setWdOpen(r); setWdForm({ tanggal: today, nominal: 0, deskripsi: "" }); }} data-testid={`tab-debit-${r.id}`}>
                  <TrendingDown className="w-3 h-3 inline mr-1 text-red-600" />Debit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CRUD Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit Rekening" : "Buka Rekening Baru"} width={500}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={save} data-testid="rk-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Nama Rekening*</label>
          <input className="vb-input" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Mis: BCA Operasional / PayPal USD" required data-testid="rk-nama" /></div>
        <div className="vb-form-row"><label className="vb-label">Nama Bank/Wallet</label>
          <input className="vb-input" value={form.bank_nama} onChange={(e) => setForm({ ...form, bank_nama: e.target.value })} placeholder="BCA, Mandiri, PayPal, Wise..." data-testid="rk-bank" /></div>
        <div className="vb-form-row"><label className="vb-label">No. Rekening / Account</label>
          <input className="vb-input" value={form.no_rekening} onChange={(e) => setForm({ ...form, no_rekening: e.target.value })} data-testid="rk-no" /></div>
        <div className="vb-form-row"><label className="vb-label">Mata Uang*</label>
          <select className="vb-select" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} disabled={!!editId} data-testid="rk-currency">
            <option value="IDR">IDR (Rupiah)</option>
            <option value="USD">USD (Dollar)</option>
          </select>
          {editId && <span style={{ fontSize: 11, color: "#666" }}>Mata uang tidak dapat diubah setelah dibuat.</span>}
        </div>
        {!editId && (
          <div className="vb-form-row"><label className="vb-label">Saldo Awal</label>
            <input type="number" step="0.01" className="vb-input" value={form.saldo_awal} onChange={(e) => setForm({ ...form, saldo_awal: e.target.value })} data-testid="rk-saldo-awal" /></div>
        )}
        <div className="vb-form-row"><label className="vb-label">Status</label>
          <label style={{ fontSize: 12 }}><input type="checkbox" checked={!!form.status_aktif} onChange={(e) => setForm({ ...form, status_aktif: e.target.checked })} /> Rekening aktif</label></div>
        <div className="vb-form-row"><label className="vb-label">Catatan</label>
          <textarea rows={2} className="vb-textarea" value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} /></div>
      </Dialog>

      {/* Topup */}
      <Dialog open={!!topupOpen} onClose={() => setTopupOpen(null)} title={`${topupOpen?.tabungan_user_id ? "Kredit" : "Topup"} ${topupOpen?.nama || ""}`} width={460}
        footer={<><button className="vb-btn" onClick={() => setTopupOpen(null)}>Batal</button><button className="vb-btn-primary" onClick={doTopup} data-testid="topup-save">Topup</button></>}>
        {topupOpen && (
          <>
            <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, marginBottom: 10, fontSize: 12 }}>
              Saldo saat ini: <strong>{fmtCur(topupOpen.saldo, topupOpen.currency)}</strong>
            </div>
            <div className="vb-form-row"><label className="vb-label">Tanggal*</label>
              <input type="date" className="vb-input" value={topupForm.tanggal} onChange={(e) => setTopupForm({ ...topupForm, tanggal: e.target.value })} /></div>
            <div className="vb-form-row"><label className="vb-label">Nominal ({topupOpen.currency})*</label>
              <input type="number" step="0.01" className="vb-input" value={topupForm.nominal} onChange={(e) => setTopupForm({ ...topupForm, nominal: e.target.value })} data-testid="topup-nominal" /></div>
            <div className="vb-form-row"><label className="vb-label">Deskripsi*</label>
              <input className="vb-input" value={topupForm.deskripsi} onChange={(e) => setTopupForm({ ...topupForm, deskripsi: e.target.value })} placeholder="Mis: Setoran modal / Pendapatan project X" data-testid="topup-deskripsi" /></div>
          </>
        )}
      </Dialog>

      {/* Withdraw */}
      <Dialog open={!!wdOpen} onClose={() => setWdOpen(null)} title={`${wdOpen?.tabungan_user_id ? "Debit" : "Pengeluaran"} ${wdOpen?.nama || ""}`} width={460}
        footer={<><button className="vb-btn" onClick={() => setWdOpen(null)}>Batal</button><button className="vb-btn-primary" onClick={doWithdraw} data-testid="wd-save">Catat</button></>}>
        {wdOpen && (
          <>
            <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, marginBottom: 10, fontSize: 12 }}>
              Saldo saat ini: <strong>{fmtCur(wdOpen.saldo, wdOpen.currency)}</strong>
              {parseFloat(wdForm.nominal || 0) > parseFloat(wdOpen.saldo || 0) && (
                <div style={{ color: "#d8000c", marginTop: 4 }}>⚠ Saldo akan menjadi minus.</div>
              )}
            </div>
            <div className="vb-form-row"><label className="vb-label">Tanggal*</label>
              <input type="date" className="vb-input" value={wdForm.tanggal} onChange={(e) => setWdForm({ ...wdForm, tanggal: e.target.value })} /></div>
            <div className="vb-form-row"><label className="vb-label">Nominal ({wdOpen.currency})*</label>
              <input type="number" step="0.01" className="vb-input" value={wdForm.nominal} onChange={(e) => setWdForm({ ...wdForm, nominal: e.target.value })} data-testid="wd-nominal" /></div>
            <div className="vb-form-row"><label className="vb-label">Keterangan*</label>
              <input className="vb-input" value={wdForm.deskripsi} onChange={(e) => setWdForm({ ...wdForm, deskripsi: e.target.value })} placeholder="Mis: Bayar listrik" /></div>
          </>
        )}
      </Dialog>

      {/* Transfer */}
      <Dialog open={trfOpen} onClose={() => setTrfOpen(false)} title="Transfer Antar Rekening" width={520}
        footer={<><button className="vb-btn" onClick={() => setTrfOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={doTransfer} data-testid="trf-save">Transfer</button></>}>
        {(() => {
          const src = reks.find((r) => r.id === trfForm.from_rekening_id);
          const dst = reks.find((r) => r.id === trfForm.to_rekening_id);
          return (
            <>
              <div className="vb-form-row"><label className="vb-label">Dari Rekening*</label>
                <select className="vb-select" value={trfForm.from_rekening_id} onChange={(e) => setTrfForm({ ...trfForm, from_rekening_id: e.target.value })} data-testid="trf-from">
                  {reks.map((r) => <option key={r.id} value={r.id}>{r.nama} ({r.currency}) — saldo {fmtCur(r.saldo, r.currency)}</option>)}
                </select></div>
              <div className="vb-form-row"><label className="vb-label">Ke Rekening*</label>
                <select className="vb-select" value={trfForm.to_rekening_id} onChange={(e) => setTrfForm({ ...trfForm, to_rekening_id: e.target.value })} data-testid="trf-to">
                  {reks.filter((r) => r.id !== trfForm.from_rekening_id).map((r) => <option key={r.id} value={r.id}>{r.nama} ({r.currency}) — saldo {fmtCur(r.saldo, r.currency)}</option>)}
                </select></div>
              <div className="vb-form-row"><label className="vb-label">Tanggal*</label>
                <input type="date" className="vb-input" value={trfForm.tanggal} onChange={(e) => setTrfForm({ ...trfForm, tanggal: e.target.value })} /></div>
              <div className="vb-form-row"><label className="vb-label">Nominal ({src?.currency || ""})*</label>
                <input type="number" step="0.01" className="vb-input" value={trfForm.nominal} onChange={(e) => setTrfForm({ ...trfForm, nominal: e.target.value })} data-testid="trf-nominal" /></div>
              <div className="vb-form-row"><label className="vb-label">Keterangan</label>
                <input className="vb-input" value={trfForm.deskripsi} onChange={(e) => setTrfForm({ ...trfForm, deskripsi: e.target.value })} /></div>
              {src && dst && src.currency !== dst.currency && parseFloat(trfForm.nominal || 0) > 0 && (
                <div style={{ background: "#e8f4ff", border: "1px solid #c4d9f1", padding: 8, fontSize: 12 }}>
                  Konversi {src.currency}→{dst.currency}: {fmtCur(trfForm.nominal, src.currency)} → ≈ <strong>{fmtCur(convert(trfForm.nominal, src.currency, dst.currency), dst.currency)}</strong>
                </div>
              )}
            </>
          );
        })()}
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus rekening "${del?.nama}"? (Hanya rekening tanpa mutasi yang bisa dihapus)`} />
    </>
  );
}

// ============ MUTASI TAB ============
function MutasiTab({ reks }) {
  const [rekId, setRekId] = useState(reks[0]?.id || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!rekId && reks[0]) setRekId(reks[0].id);
  }, [reks]);

  const load = async () => {
    if (!rekId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/rekening/${rekId}/mutasi`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rekId]);

  const cur = reks.find((r) => r.id === rekId);

  return (
    <>
      <div style={{ marginBottom: 10, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <label className="vb-label" style={{ marginBottom: 0, marginRight: 4 }}>Rekening:</label>
        <select className="vb-select" style={{ width: 280 }} value={rekId} onChange={(e) => setRekId(e.target.value)} data-testid="mutasi-rk">
          {reks.map((r) => <option key={r.id} value={r.id}>{r.nama} ({r.currency})</option>)}
        </select>
        <button className="vb-btn" onClick={load}><RefreshCw className="w-3 h-3" /></button>
      </div>
      {loading ? <Loading /> : <MutasiTable items={items} fmt={(n) => fmtCur(n, cur?.currency)} mode="admin" />}
    </>
  );
}

// ============ CASHFLOW TAB (Manual entries) ============
function CashflowTab({ reks, convert, display }) {
  const today = new Date().toISOString().slice(0, 10);
  const [items, setItems] = useState([]);
  const [saldo, setSaldo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tanggal: today, jenis: "Masuk", kategori: "Penjualan", deskripsi: "", nominal: 0, metode: "Transfer" });
  const [del, setDel] = useState(null);
  const [filterJenis, setFilterJenis] = useState("");
  const [filterBulan, setFilterBulan] = useState(today.slice(0, 7));

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterJenis) params.jenis = filterJenis;
      if (filterBulan) params.bulan = filterBulan;
      const [c, s] = await Promise.all([api.get("/cashflow", { params }), api.get("/cashflow/saldo")]);
      setItems(c.data); setSaldo(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterJenis, filterBulan]);

  const submit = async (e) => {
    e?.preventDefault?.();
    try {
      await api.post("/cashflow", { ...form, nominal: parseFloat(form.nominal) });
      toast.success("Tersimpan"); setOpen(false); setForm({ tanggal: today, jenis: "Masuk", kategori: "Penjualan", deskripsi: "", nominal: 0, metode: "Transfer" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/cashflow/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <button className="vb-btn-primary" onClick={() => setOpen(true)} data-testid="add-cashflow">
          <Plus className="w-3 h-3 inline mr-1" />Catat Transaksi Manual
        </button>
        <span style={{ fontSize: 11, color: "#666", marginLeft: 10 }}>Catatan: Arus Kas Manual ini terpisah dari saldo Rekening. Gunakan untuk pencatatan tambahan / lintas-rekening.</span>
      </div>
      {saldo && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginBottom: 10 }}>
          <div className="vb-statcard"><div className="vb-statcard-label">Total Masuk (all-time)</div><div className="vb-statcard-value" style={{ color: "#4f8a10" }}>{fmtIDR(saldo.total_masuk)}</div></div>
          <div className="vb-statcard"><div className="vb-statcard-label">Total Keluar (all-time)</div><div className="vb-statcard-value" style={{ color: "#d8000c" }}>{fmtIDR(saldo.total_keluar)}</div></div>
          <div className="vb-statcard"><div className="vb-statcard-label">Saldo Cashflow Manual</div><div className="vb-statcard-value" style={{ color: saldo.saldo >= 0 ? "#003399" : "#d8000c" }}>{fmtIDR(saldo.saldo)}</div></div>
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        <input type="month" className="vb-input" style={{ width: 160 }} value={filterBulan} onChange={(e) => setFilterBulan(e.target.value)} />
        <select className="vb-select" style={{ width: 150 }} value={filterJenis} onChange={(e) => setFilterJenis(e.target.value)}>
          <option value="">Semua Jenis</option><option>Masuk</option><option>Keluar</option>
        </select>
      </div>
      {loading ? <Loading /> : items.length === 0 ? <EmptyState message="Belum ada transaksi" /> : (
        <div style={{ overflowX: "auto" }}>
          <table className="vb-table">
            <thead><tr><th>Tanggal</th><th>Jenis</th><th>Kategori</th><th>Deskripsi</th><th>Metode</th><th>Sumber</th><th style={{ textAlign: "right" }}>Nominal</th><th>Aksi</th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>{it.tanggal}</td>
                  <td>{it.jenis === "Masuk" ? <span className="vb-badge vb-badge-approved">Masuk</span> : <span className="vb-badge vb-badge-rejected">Keluar</span>}</td>
                  <td>{it.kategori}</td><td>{it.deskripsi}</td><td>{it.metode}</td>
                  <td>{it.source === "manual" ? <span style={{ fontSize: 11, color: "#666" }}>Manual</span> : <span className="vb-badge vb-badge-izin">{it.source}</span>}</td>
                  <td style={{ textAlign: "right", color: it.jenis === "Masuk" ? "#4f8a10" : "#d8000c", fontWeight: 600 }}>{it.jenis === "Masuk" ? "+ " : "- "}{fmtIDR(it.nominal)}</td>
                  <td>{it.source !== "payroll" && it.source !== "hutang" && it.source !== "kasbon" && (
                    <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)}><Trash2 className="w-3 h-3" /></button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title="Catat Transaksi Manual (Arus Kas)" width={500}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={submit} data-testid="cf-save">Simpan</button></>}>
        <form onSubmit={submit}>
          <div className="vb-form-row"><label className="vb-label">Jenis*</label>
            <select className="vb-select" value={form.jenis} onChange={(e) => setForm({ ...form, jenis: e.target.value, kategori: e.target.value === "Masuk" ? "Penjualan" : "Operasional" })}>
              <option>Masuk</option><option>Keluar</option>
            </select></div>
          <div className="vb-form-row"><label className="vb-label">Kategori*</label>
            <select className="vb-select" value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value })}>
              {(form.jenis === "Masuk" ? KATEGORI_MASUK : KATEGORI_KELUAR).map((k) => <option key={k}>{k}</option>)}
            </select></div>
          <div className="vb-form-row"><label className="vb-label">Tanggal*</label>
            <input type="date" className="vb-input" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} required /></div>
          <div className="vb-form-row"><label className="vb-label">Nominal (IDR)*</label>
            <input type="number" step="0.01" className="vb-input" value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value })} required /></div>
          <div className="vb-form-row"><label className="vb-label">Metode*</label>
            <select className="vb-select" value={form.metode} onChange={(e) => setForm({ ...form, metode: e.target.value })}>
              <option>Transfer</option><option>Cash</option>
            </select></div>
          <div className="vb-form-row"><label className="vb-label">Deskripsi*</label>
            <textarea rows={2} className="vb-textarea" value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} required /></div>
        </form>
      </Dialog>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus transaksi "${del?.deskripsi}"?`} />
    </>
  );
}
